import { useEffect, useRef, useSyncExternalStore } from 'react';
import { TabulatorFull as Tabulator, type CellComponent, type ColumnDefinition, type Options } from 'tabulator-tables';
import { t } from '../../i18n';
import { getGridSettings, getZoom, saveGridSettings, setZoom, subscribeSettings, ZOOM_STEP } from '../../settings';
import { registerGrid } from './clipboard';
import { enableUndo, pushSnapshot, snapshot } from './undo';
import './tabulator.css';

export type { ColumnDefinition } from 'tabulator-tables';

interface Props {
  /** Имя таблицы: под ним сохраняются ширины столбцов в настройках пользователя. */
  gridId: string;
  columns: ColumnDefinition[];
  data: unknown[];
  height?: string;
  options?: Options;
  /** sheet — таблица ввода (можно вставлять и править), list — таблица просмотра. */
  variant?: 'sheet' | 'list';
  /** Пустая строка — для таблиц ввода: добавляется, когда вставляемый блок длиннее таблицы. */
  blankRow?: () => Record<string, string>;
  /** Данные изменены вставкой или вырезанием. */
  onChanged?: () => void;
  /** Таблица создана — можно обратиться к ней напрямую. */
  onReady?: (table: Tabulator) => void;
}

/** Иконки сортировки и дерева — тонкие стрелки вместо стандартных треугольников и плюсов. */
const SORT_ICON =
  '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 15 6-6 6 6"/></svg>';
const TREE_OPEN =
  '<span class="tree-toggle" data-open="1"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg></span>';
const TREE_CLOSED =
  '<span class="tree-toggle"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></span>';

/** Tabulator иногда отдаёт внутренний объект ячейки вместо компонента — приводим к компоненту. */
function asComponent(cell: unknown): CellComponent | null {
  if (!cell) return null;
  const c = cell as { getColumn?: unknown; getComponent?: () => CellComponent };
  if (typeof c.getColumn === 'function') return cell as CellComponent;
  return typeof c.getComponent === 'function' ? c.getComponent() : null;
}

/** Одна выделенная ячейка (или ничего). */
function singleCell(table: Tabulator): CellComponent | null {
  const ranges = table.getRanges();
  const range = ranges[ranges.length - 1];
  if (!range) return null;
  const bounds = range.getBounds();
  const start = asComponent(bounds.start);
  const end = asComponent(bounds.end);
  if (!start || !end || start.getElement() !== end.getElement()) return null;
  return start;
}

/**
 * Таблица приложения на Tabulator (открытая библиотека, MIT), настроенная как Excel:
 * - щелчок выделяет ячейку, двойной щелчок, Enter, F2 или начало набора — правка;
 * - протягивание мышью и Shift+стрелки выделяют диапазон, щелчок по заголовку — столбец, по номеру строки — строку;
 * - Ctrl+C / Ctrl+X / Ctrl+V — через clipboard.ts, вставка блока из Excel с выделенной ячейки;
 * - Ctrl+Z / Ctrl+Y в таблицах ввода — отмена и повтор (undo.ts), вставка отменяется целиком;
 * - щелчок мимо таблицы снимает выделение;
 * - масштаб (Ctrl+колёсико над таблицей или кнопки в шапке), ширины столбцов — запоминаются для пользователя.
 * Данные выровнены по центру (кроме столбца-дерева), длинный текст переносится на новую строку.
 */
export function TableGrid({ gridId, columns, data, height, options, variant = 'list', blankRow, onChanged, onReady }: Props) {
  const frame = useRef<HTMLDivElement>(null);
  const holder = useRef<HTMLDivElement>(null);
  const table = useRef<Tabulator | null>(null);
  const ready = useRef(false);
  const callbacks = useRef({ blankRow, onChanged });
  callbacks.current = { blankRow, onChanged };
  const zoom = useSyncExternalStore(subscribeSettings, getZoom);
  const editable = variant === 'sheet';

  useEffect(() => {
    if (!holder.current || !frame.current) return;
    const element = holder.current;
    const box = frame.current;
    const size = fitToFrame(box, element, getZoom());
    const instance = new Tabulator(element, {
      data,
      columns,
      height: height ?? size.height,
      layout: 'fitColumns',
      reactiveData: false,
      placeholder: t('Нет строк'),
      // Выделение диапазонов: ячейки, столбцы (по заголовку) и строки (по номеру слева)
      selectableRange: true,
      selectableRangeColumns: true,
      selectableRangeRows: true,
      // Delete / Backspace обрабатывает clipboard.ts: встроенная очистка Tabulator стирает только одну ячейку
      selectableRangeClearCells: false,
      // Без выделения до первого щелчка (опции нет в описании типов Tabulator)
      ...({ selectableRangeInitializeDefault: false } as Options),
      rowHeader: {
        formatter: 'rownum',
        headerSort: false,
        resizable: false,
        frozen: true,
        hozAlign: 'center',
        headerHozAlign: 'center',
        width: 46,
        minWidth: 40,
        cssClass: 'row-num'
      } as unknown as Options['rowHeader'],
      // Буфер обмена обрабатывает clipboard.ts
      clipboard: false,
      editTriggerEvent: 'dblclick',
      movableColumns: true,
      headerSortElement: SORT_ICON,
      dataTreeExpandElement: TREE_CLOSED,
      dataTreeCollapseElement: TREE_OPEN,
      dataTreeBranchElement: false,
      columnDefaults: {
        resizable: true,
        hozAlign: 'center',
        headerHozAlign: 'center',
        vertAlign: 'middle',
        headerWordWrap: true,
        minWidth: 60,
        // В таблицах ввода щелчок по заголовку выделяет столбец, а не сортирует
        headerSort: !editable
      },
      persistence: { columns: ['width', 'visible'] },
      persistenceID: gridId,
      persistenceMode: 'local',
      persistenceWriterFunc: (id: string, type: string, value: unknown) => saveGridSettings(`${id}:${type}`, value),
      persistenceReaderFunc: (id: string, type: string) => getGridSettings(`${id}:${type}`),
      ...options
    });
    table.current = instance;

    let unregister: (() => void) | null = null;
    // Таблица ввода: отмена изменений Ctrl+Z (снимок до правки ячейки)
    let beforeEdit: string | null = null;
    if (editable) {
      enableUndo(instance);
      instance.on('cellEditing', () => {
        beforeEdit = snapshot(instance);
      });
      instance.on('cellEdited', () => {
        if (beforeEdit !== null) pushSnapshot(instance, beforeEdit);
        beforeEdit = null;
      });
      instance.on('cellEditCancelled', () => {
        beforeEdit = null;
      });
    }
    instance.on('tableBuilt', () => {
      ready.current = true;
      unregister = registerGrid({
        table: instance,
        element,
        editable,
        blankRow: () => callbacks.current.blankRow?.() ?? {},
        onChanged: () => callbacks.current.onChanged?.()
      });
      onReady?.(instance);
    });
    // Текст переносится: после изменения ширины столбца высота строк пересчитывается
    instance.on('columnResized', () => instance.redraw(true));
    // Шрифт догружается после построения таблицы — пересчитать высоты под настоящие размеры текста
    void document.fonts?.ready.then(() => {
      if (ready.current) instance.redraw(true);
    });

    // Набор текста на выделенной ячейке сразу начинает правку (как в Excel), F2 — правка с текущим значением
    const onKey = (e: KeyboardEvent) => {
      if (!ready.current || e.ctrlKey || e.metaKey || e.altKey) return;
      const typing = e.key.length === 1;
      if (!typing && e.key !== 'F2') return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.closest('.tabulator-editing')) return;
      const cell = singleCell(instance);
      if (!cell || !cell.getColumn().getDefinition().editor) return;
      e.preventDefault();
      cell.edit(true);
      const input = cell.getElement().querySelector('input');
      if (input && typing) {
        input.value = e.key;
        requestAnimationFrame(() => {
          input.value = e.key;
          input.setSelectionRange(1, 1);
        });
      }
    };
    // Ctrl+колёсико над таблицей — масштаб таблиц, как в Excel
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoom(getZoom() + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
    };
    // Enter на ячейке, которую править нельзя (серые, заполняет программа), в Tabulator вызывает ошибку — гасим заранее
    const onEnterGuard = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !ready.current) return;
      const target = e.target as HTMLElement;
      if (target.closest('.tabulator-editing')) return;
      const cell = singleCell(instance);
      if (cell && !cell.getColumn().getDefinition().editor) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    element.addEventListener('keydown', onEnterGuard, true);
    element.addEventListener('keydown', onKey);
    element.addEventListener('wheel', onWheel, { passive: false });

    // Размер таблицы следует за местом на экране (окно, панель, масштаб)
    const observer = new ResizeObserver(() => {
      const next = fitToFrame(box, element, getZoom());
      if (ready.current && !height) instance.setHeight(next.height);
    });
    observer.observe(box);

    return () => {
      observer.disconnect();
      element.removeEventListener('keydown', onEnterGuard, true);
      element.removeEventListener('keydown', onKey);
      element.removeEventListener('wheel', onWheel);
      unregister?.();
      ready.current = false;
      instance.destroy();
      table.current = null;
    };
    // Таблица создаётся один раз; данные и столбцы обновляются ниже
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridId]);

  // Масштаб: вся таблица увеличивается или уменьшается целиком, затем Tabulator пересчитывает строки
  useEffect(() => {
    const element = holder.current;
    const box = frame.current;
    if (!element || !box) return;
    const size = fitToFrame(box, element, zoom);
    if (ready.current && table.current) {
      if (!height) table.current.setHeight(size.height);
      table.current.redraw(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  // Новые описания столбцов (например, после смены языка) — с сохранением текущих ширин
  useEffect(() => {
    const grid = table.current;
    if (!ready.current || !grid) return;
    const widths = new Map<string, number>();
    for (const col of grid.getColumns()) {
      const field = col.getField();
      if (field) widths.set(field, col.getWidth());
    }
    const next = columns.map((c) => (c.field && widths.has(c.field) ? { ...c, width: widths.get(c.field) } : c));
    void grid.setColumns(next);
  }, [columns]);

  useEffect(() => {
    if (ready.current && table.current) void table.current.replaceData(data as never[]);
  }, [data]);

  return (
    <div ref={frame} className="parts-table-frame">
      <div ref={holder} className={`parts-table parts-table--${variant}`} />
    </div>
  );
}

/**
 * Масштаб делается свойством CSS zoom. Tabulator сам неверно считает высоту увеличенной таблицы,
 * поэтому размер задаётся явно: место на экране, делённое на масштаб.
 */
function fitToFrame(box: HTMLElement, element: HTMLElement, zoomPct: number): { width: number; height: number } {
  const z = zoomPct / 100;
  const width = Math.max(200, Math.floor(box.clientWidth / z));
  const height = Math.max(120, Math.floor(box.clientHeight / z));
  element.style.zoom = String(z);
  element.style.width = `${width}px`;
  element.style.height = `${height}px`;
  return { width, height };
}
